import React, { useState } from 'react';
import styled from 'styled-components';

import { Form, Space, Table, Typography, Button, Divider, Alert } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import FormItemFactory from './FormItemFactory';
import WithRenderingQuestion from './WithRenderingQuestion';
import { getTranslatedString } from 'helpers/i18n';
import { removeFromList } from 'helpers/ListHelper';

const { Title } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;
  position: relative;
`;

const StyledForm = styled(Form)`
  padding: 24px;
  background: #fbfbfb;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
`;

const MultiInstancesForm = ({
  form,
  formTitle,
  formDescription,
  fields,
  items,
  formMinInstances,
  formMaxInstances,
  hasRenderingQuestion,
  renderingQuestion,
  renderingQuestionResponse,
}) => {
  const [editableItem, setEditableFormItem] = useState(null);
  const [itemsLength, setItemsLength] = useState(items.length);

  let warningMessage;

  const deleteFormItem = (index) => {
    const currentItems = form.getFieldValue('items') || [];
    const [, newSourceList] = removeFromList(currentItems, index);
    form.setFieldsValue({ items: newSourceList });
    setItemsLength(form.getFieldValue('items').length);
  };

  const columns = [
    {
      title: formTitle,
      dataIndex: 'title',
      render: (_, record) => Object.values(record)[0],
    },
    {
      key: 'actions',
      render: (_, record, index) => (
        <>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => setEditableFormItem({ currentIndex: index, record })}
          />
          <Button type="link" icon={<DeleteOutlined />} onClick={() => deleteFormItem(index)} />
        </>
      ),
      width: '20%',
    },
  ];

  const onFinish = (newRecord) => {
    const currentItems = form.getFieldValue('items') || [];
    let resultItems;

    if (editableItem.currentIndex != null) {
      resultItems = currentItems.map((item, index) => {
        if (index === editableItem.currentIndex) {
          return newRecord;
        }

        return item;
      });
    } else {
      resultItems = [...currentItems, newRecord];
    }

    form.setFieldsValue({ items: resultItems });
    setItemsLength(form.getFieldValue('items').length);
    setEditableFormItem(null);
  };

  const AddItemForm = () => (
    <StyledForm layout="vertical" onFinish={onFinish} initialValues={editableItem.record}>
      <StyledSpace direction="vertical" size="middle">
        {fields.map((field) => (
          <FormItemFactory
            key={field.id}
            required={field.required}
            label={field.label}
            name={field.name}
            placeholder={field.placeholder}
            prototypeOf={field.field_type}
            availableOptions={field.available_options}
          />
        ))}

        <Divider />

        <Form.Item noStyle shouldUpdate>
          {({ getFieldsValue }) => (
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                disabled={Object.values(getFieldsValue()).every((field) => !field)}
              >
                {getTranslatedString('add')}
              </Button>

              <Button htmlType="button" onClick={() => setEditableFormItem(null)}>
                {getTranslatedString('cancel')}
              </Button>
            </Space>
          )}
        </Form.Item>
      </StyledSpace>
    </StyledForm>
  );

  const showWarning = itemsLength < formMinInstances || itemsLength > formMaxInstances;

  if (showWarning) {
    warningMessage =
      itemsLength < formMinInstances
        ? `At least ${formMinInstances} must be added`
        : `No more than ${formMaxInstances} can be added`;
  }

  if (editableItem) {
    return (
      <>
        <Title level={5}>{formTitle}</Title>
        <div dangerouslySetInnerHTML={{ __html: formDescription }}></div>
        <AddItemForm />
      </>
    );
  } else {
    return (
      <>
        <Form
          form={form}
          initialValues={{ rendering_question_response: renderingQuestionResponse, items }}
        >
          <Title level={5}>{formTitle}</Title>
          <div dangerouslySetInnerHTML={{ __html: formDescription }}></div>
          {showWarning && <Alert message={warningMessage} type="warning" showIcon />}

          <WithRenderingQuestion
            hasRenderingQuestion={hasRenderingQuestion}
            renderingQuestion={renderingQuestion}
          >
            <Form.Item
              shouldUpdate={(prevValues, curValues) => prevValues.items !== curValues.items}
            >
              {({ getFieldValue }) => {
                const items = getFieldValue('items') || [];

                return (
                  <>
                    {items.length > 0 && (
                      <Table
                        bordered
                        dataSource={items}
                        columns={columns}
                        hideActionAndSearch
                        disableInternalSearch={true}
                        size={'small'}
                      />
                    )}
                  </>
                );
              }}
            </Form.Item>

            {itemsLength < formMaxInstances && (
              <Button onClick={() => setEditableFormItem({})}>
                {getTranslatedString('add')} {formTitle}
              </Button>
            )}
          </WithRenderingQuestion>
        </Form>
      </>
    );
  }
};

export default MultiInstancesForm;
